import { toast } from 'sonner'
import { paths } from '@/routes/paths'
import { useState, useEffect } from 'react'
import { fError } from '@/utils/format-error'
// Components
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
// API & Types
import axiosInstance, { endpoints } from '@/lib/axios'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'

import ProductsTable from '../products-table'
import ProductsFilter from '../products-filter'
import {
  Product,
  ProductFilters,
  ProductStatus,
  ProductsPaginationResponse,
} from '../product.types'

// Constants
const PAGE_SIZE = 10

export default function ProductsView() {
  const router = useRouter()
  const loading = useBoolean(true)

  // State
  const [products, setProducts] = useState<Product[]>([])
  const [pageMeta, setPageMeta] = useState({
    total: 0,
    page: 0,
    limit: 0,
  })

  const [filters, setFilters] = useState<ProductFilters>({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    status: ProductStatus.ALL,
    orderBy: 'updatedAt:desc',
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    productId: string | null
  }>({
    open: false,
    productId: null,
  })

  // Loading state for delete operation
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Fetch products data
  const fetchProducts = async () => {
    try {
      loading.onTrue()

      // เตรียม parameters สำหรับ API
      const apiParams = {
        page: filters.page,
        limit: filters.limit,
        orderBy: filters.orderBy,
        ...(filters.search && { search: filters.search }),
        ...(filters.status !== ProductStatus.ALL && {
          isActive: filters.status === ProductStatus.ACTIVE,
        }),
      }

      const { data } = await axiosInstance.get<
        ProductsPaginationResponse<Product>
      >(endpoints.products.myProducts, { params: apiParams })

      setProducts(data.items)
      setPageMeta({
        total: data.total,
        page: filters.page,
        limit: filters.limit,
      })
    } catch (error) {
      console.error('Error fetching products:', error)
      toast.error(fError(error))
      setProducts([])
    } finally {
      loading.onFalse()
    }
  }

  // Initial load และ refetch เมื่อ filters เปลี่ยน
  useEffect(() => {
    fetchProducts()
  }, [filters])

  // Handle filter changes
  const handleFiltersChange = (newFilters: Partial<ProductFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  // Navigation handlers
  const handleCreateNew = () => {
    router.push(paths.productCreate)
  }

  const handleEdit = (productId: string) => {
    router.push(paths.productEdit(productId))
  }

  const handleDelete = (productId: string) => {
    setConfirmDialog({
      open: true,
      productId,
    })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog.productId || deleteLoading) return

    try {
      setDeleteLoading(true)

      await axiosInstance.delete(
        endpoints.products.delete(confirmDialog.productId),
      )
      toast.success('ลบสินค้าสำเร็จ')

      // Refresh data
      await fetchProducts()
    } catch (error) {
      console.error('Error deleting product:', error)
      toast.error(fError(error))
    } finally {
      setDeleteLoading(false)
      setConfirmDialog({ open: false, productId: null })
    }
  }

  return (
    <div className="w-full">
      <LoadingSection loading={loading.value}>
        <div className="space-y-6">
          {/* Filter Section */}
          <ProductsFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onCreateNew={handleCreateNew}
          />

          {/* Table Section */}
          <ProductsTable
            products={products}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading.value}
          />

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <CustomPagination
              currentPage={filters.page}
              totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, productId: null })}
        title="ยืนยันการลบ"
        description="คุณต้องการลบสินค้านี้หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้"
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  )
}
